import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CountryGateway } from 'src/app/application/gateway/CountryGateway';
import { ProfileGateway } from 'src/app/application/gateway/ProfileGateway';
import { ConsigneeNotifierAdminService } from 'src/app/application/service/ConsigneeNotifierAdminService';
import { CreateConsigneeOrNotifierFromUserRequest } from 'src/app/domain/models/ConsigneeNotifierAdmin';
import { Country } from 'src/app/domain/models/Country';
import { Profile } from 'src/app/domain/models/Profile';
import { ProfileUtils } from 'src/app/ui/util/profile-utils';

@Component({
  selector: 'app-admin-user-detail',
  templateUrl: './admin-user-detail.component.html',
  styleUrls: ['./admin-user-detail.component.css'],
})
export class AdminUserDetailComponent {
  @Input() profileId: number | undefined;
  @Output() closeDialog = new EventEmitter();

  profileTypeOptions: any[] = ProfileUtils.getProfileTypeOptions();
  profile: Profile | undefined;
  countries: Country[] = [];
  showImportersAssociationDialog: boolean = false;

  formGroup: FormGroup = this._formBuilder.group({
    userId: [{ value: '', disabled: true }],
    profileType: [{ value: '', disabled: true }],
    contactName: [{ value: '', disabled: true }],
    contactLastName: [{ value: null, disabled: true }],
    contactCountryId: [{ value: null, disabled: true }],
    contactCity: [{ value: '', disabled: true }],
    contactAddress: [{ value: '', disabled: true }],
    contactPhone: [{ value: '', disabled: true }],
    vat: [{ value: '', disabled: true }],
    companyName: [{ value: '', disabled: true }],
    legalRepresentativeName: [{ value: '', disabled: true }],
    legalRepresentativeNumber: [{ value: null, disabled: true }],
  });

  constructor(
    private readonly _messageService: MessageService,
    private readonly _formBuilder: FormBuilder,
    private readonly _profileService: ProfileGateway,
    private readonly _countryService: CountryGateway,
    private readonly _consigneeNotifierAdminService: ConsigneeNotifierAdminService,
    private readonly _confirmationService: ConfirmationService,
    public translate: TranslateService
  ) {}

  ngOnInit(): void {
    if (this.profileId) {
      this.loadCountries();
      this.loadProfile();
    }
  }

  loadForm() {
    if (this.profile) {
      this.formGroup.controls['userId'].setValue(this.profile.id);
      this.formGroup.controls['profileType'].setValue(this.profile.type);
      this.formGroup.controls['contactName'].setValue(this.profile.contactName);
      this.formGroup.controls['contactLastName'].setValue(
        this.profile.contactLastName
      );
      this.formGroup.controls['contactCountryId'].setValue(
        this.profile.contactCountryId
      );
      this.formGroup.controls['contactCity'].setValue(this.profile.contactCity);
      this.formGroup.controls['contactAddress'].setValue(
        this.profile.contactAddress
      );
      this.formGroup.controls['contactPhone'].setValue(
        this.profile.contactPhone
      );
      this.formGroup.controls['vat'].setValue(this.profile.vat);
      this.formGroup.controls['companyName'].setValue(this.profile.companyName);
      this.formGroup.controls['legalRepresentativeName'].setValue(
        this.profile.legalRepresentativeName
      );
      this.formGroup.controls['legalRepresentativeNumber'].setValue(
        this.profile.legalRepresentativeNumber
      );
    }
  }

  loadProfile(): void {
    if (this.profileId) {
      this._profileService.getById(this.profileId).subscribe({
        next: (v) => {
          this.profile = v;
          this.loadForm();
        },
        error: (e) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Acción fallida',
            detail: 'No se pudo recuperar el perfil.',
          });
        },
      });
    }
  }

  loadCountries(): void {
    this._countryService.getAll().subscribe({
      next: (v) => {
        this.countries = v;
      },
      error: (e) => {
        this._messageService.add({
          severity: 'error',
          summary: 'Acción fallida',
          detail: 'No se pudo recuperar la lista de paises.',
        });
      },
    });
  }

  createConsigneeOrNotifier(type: string) {
    if (this.profileId) {
      this._confirmationService.confirm({
        message: this.translate.instant(
          'AdminUsersManagement.CreateConsigneeNotifierFromUser',
          { type: type[0].toUpperCase() + type.substring(1) }
        ),
        header: this.translate.instant(
          'AdminUsersManagement.CreateConsigneeNotifierFromUserHeader',
          { type: type[0].toUpperCase() + type.substring(1) }
        ),
        icon: 'pi pi-users',
        acceptLabel: this.translate.instant(
          'MessageService.ConsigneeNotifier.CreateButton'
        ),
        rejectLabel: this.translate.instant(
          'MessageService.ConsigneeNotifier.CancelButton'
        ),
        acceptIcon: 'pi',
        rejectIcon: 'pi',
        accept: () => {
          if (this.profileId) {
            let request: CreateConsigneeOrNotifierFromUserRequest = {
              userId: this.profileId,
              type: type,
            };

            this._consigneeNotifierAdminService
              .CreateConsigneeOrNotifierFromUser(request)
              .subscribe({
                next: (v) => {
                  this._messageService.add({
                    severity: 'info',
                    summary: type + ' creado',
                    detail: type + ' creado correctamente.',
                  });
                },
                error: (e) => {
                  this._messageService.add({
                    severity: 'error',
                    summary: 'Acción fallida',
                    detail: 'No se pudo crear el ' + type,
                  });
                },
              });
          }
        },

        reject: (type: any) => {
          switch (type) {
          }
        },
      });
    }
  }

  onOpenImportersAssociationDialog() {
    this.showImportersAssociationDialog = true;
  }

  onCloseImportersAssociationDialog() {
    this.showImportersAssociationDialog = false;
  }

  cancel(): void {
    this.closeDialog.emit();
  }
}
